import { formatDate } from "@/utils";
import SendIcon from "@mui/icons-material/Send";
import {
    Box,
    CircularProgress,
    Link,
    Skeleton,
    Stack,
    Typography,
} from "@mui/material";
import { formatDistance } from "date-fns";

export type InvitationStatusValue =
    | { kind: "loading" }
    | { kind: "missing" }
    | { kind: "accepted"; acceptedAt: string }
    | { kind: "pending"; sentAt: string };

interface IProps {
    status: InvitationStatusValue;
    onResend: () => void;
    isResending: boolean;
    /**
     * Whether to show the send icon next to "Invitation sent...". Defaults
     * to true (the details-page look); the admin users table hides it to
     * keep the row compact.
     */
    showIcon?: boolean;
}

/**
 * Pure presentation of an invitation's status + (when pending) a Resend
 * button. No data fetching — callers (the details page, the admin users
 * table) supply `status` from whichever source they have. Keeping this
 * markup in one place is what makes the two surfaces an exact copy of
 * each other, per the "reuse the code that supplies the value" ticket
 * requirement.
 */
export const InvitationStatusDisplay = ({
    status,
    onResend,
    isResending,
    showIcon = true,
}: IProps) => {
    if (status.kind === "loading") {
        return (
            <Skeleton
                variant="text"
                width={100}
                height={24}
                aria-label="Loading invitation status"
            />
        );
    }

    if (status.kind === "missing") {
        return <Typography variant="body2">N/A</Typography>;
    }

    if (status.kind === "accepted") {
        return (
            <Typography variant="body2">
                Accepted on {formatDate(status.acceptedAt)}
            </Typography>
        );
    }

    return (
        <Stack direction="row" spacing={1} alignItems="start">
            {showIcon && <SendIcon color="primary" fontSize="small" />}
            <Stack>
                <Typography variant="body2">
                    Invitation sent{" "}
                    {formatDistance(new Date(status.sentAt), new Date(), {
                        addSuffix: true,
                    })}
                </Typography>
                <Box>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={onResend}
                        disabled={isResending}
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        Resend Invitation
                        {isResending && <CircularProgress size={12} />}
                    </Link>
                </Box>
            </Stack>
        </Stack>
    );
};
